import { Fragment, useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Copy,
  Download,
  Filter as FilterIcon,
  FilterX,
  GripVertical,
  Lock,
  LockOpen,
  X,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PaginationControls } from "@/components/ui/PaginationControls/PaginationControls";
import { CLICKS_LIMIT_OPTIONS, useGscFreshness } from "@/hooks/useClicksData";
import { getDateRangeDates } from "@/lib/compare-date-utils";
import { cn } from "@/lib/utils";
import { filterKeyForGroupBy } from "@/config/dimensions";
import { PrimaryDimSwitch } from "@/components/clicks/PrimaryDimSwitch";
import { CellContextMenu, type CellTarget } from "./CellContextMenu";
import { ColumnFilterForm } from "./filters/ColumnFilterForm";
import { MarkMenu, type MarkTarget } from "./MarkMenu";
import { useClicksMarks, useSetClicksMark } from "@/hooks/useClicksMarks";
import { useCellFilter, useUrlFilters } from "@/hooks/useUrlFilters";
import {
  MAX_FILTER_VALUES,
  excludeValues,
  fieldFilterOf,
  isMarkKey,
  isTextMode,
  keepOnlyValues,
  modeWord,
  markFilterKey,
  withFieldFilter,
} from "@/lib/filter-model";
import { DomainPages } from "./DomainPages";
import { ClicksTableRow } from "./ClicksTableRow";
// keyword volume
import { KeywordVolumeCell } from "./KeywordVolumeCell";
import { KEYWORD_VOLUME_DIMS, useKeywordVolumes } from "@/hooks/useKeywordVolumes";
import { seriesToMonths, volumeMetrics } from "@/lib/keyword-volume-parser";
import type { DateRangeOption } from "./DateRangeFilter";
import type { UrlActiveFilters } from "@/lib/url-active-filters";
import type { CampaignStat } from "@/types/clicks";
import Papa from "papaparse";
import { applyCustomMetrics, type CustomMetric } from "@/lib/custom-metrics";
import {
  CLICKS_CELL_CLASS,
  getDimensionLabel,
  getMetricHeaderTone,
  getVisibleMetrics,
  gridColumnKey,
  isAttributeColumn,
  isNumericAttribute,
  metricLabel,
  type GridColumn,
} from "./metricDisplay";
import { COMPARE_MAX } from "./chartData";
import { clicksCsv } from "./clicksCsv";
import { trackBodyHeight } from "./pinHeader";
import {
  applyHeaderClick,
  columnValue,
  captureRate,
  compareByRules,
  draftToFilter,
  filterToDraft,
  gscDaysInRange,
  filterRows,
  appliesToGrouping,
  keepAfterRegroup,
  TEXT_COLUMNS,
  orderedRules,
  selectAllState,
  selectedRowsInOrder,
  toggleLock,
  toggleSelectAll,
  uepc,
  type ColKind,
  type ColumnFilter,
  type FilterDraft,
  type SortRule,
} from "./clicksGridEngine";

interface ClicksGridProps {
  data: CampaignStat[];
  previousData?: CampaignStat[];
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  visibleColumns: string[];
  groupBy: string;
  /** All grouping dimensions; 2+ = the dimension column is a composite label. */
  groupDims?: string[];
  /** Flip the primary grouping from the first column header (Domain ⇄ URL). */
  onPrimaryDimChange?: (dim: string) => void;
  compareEnabled: boolean;
  dateRange: DateRangeOption;
  customFrom?: string;
  customTo?: string;
  activeFilters: UrlActiveFilters;
  onVisibleColumnsChange?: (columns: string[]) => void;
  /** Drill-down: scope the trend chart to a cell's value (from the context menu). */
  onChartFocus?: (focus: { key: string; value: string; label: string }) => void;
  /** Compare-on-chart: which dimension values are checked to overlay, + toggle. */
  compareValues?: string[];
  onToggleCompare?: (value: string) => void;
  /** Replace the whole selection (header checkbox, Clear). */
  onSelectValues?: (values: string[]) => void;
  /** User-defined metrics, computed per row here, after keyword volume is merged in. */
  customMetrics?: CustomMetric[];
  /**
   * The column filters (header popovers). They live in the URL (`where`) and
   * show as chips in the filter bar, so the page owns them (owner, 30.10.2026);
   * the grid only applies and edits them.
   */
  columnFilters?: Record<string, ColumnFilter>;
  onColumnFiltersChange?: (next: Record<string, ColumnFilter>) => void;
}

const NO_COLUMN_FILTERS: Record<string, ColumnFilter> = {};

/**
 * A metric header cell the user can drag to reorder columns. The grip carries
 * the drag listeners so the rest of the header keeps its sort-click and filter.
 */
function SortableMetricHead({
  id,
  className,
  onContextMenu,
  children,
}: {
  id: string;
  className?: string;
  onContextMenu?: (event: React.MouseEvent) => void;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    zIndex: isDragging ? 20 : undefined,
  };
  return (
    <TableHead
      ref={setNodeRef}
      style={style}
      className={className}
      onContextMenu={onContextMenu}
    >
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          {...attributes}
          {...listeners}
          title="Drag to reorder column"
          aria-label="Drag to reorder column"
          className="cursor-grab touch-none text-muted-foreground/40 hover:text-foreground active:cursor-grabbing"
        >
          <GripVertical className="size-3.5" />
        </button>
        {children}
      </span>
    </TableHead>
  );
}

export function ClicksGrid({
  data,
  previousData,
  pageSize,
  onPageSizeChange,
  visibleColumns,
  groupBy,
  groupDims,
  onPrimaryDimChange,
  compareEnabled,
  dateRange,
  customFrom,
  customTo,
  activeFilters,
  onVisibleColumnsChange,
  onChartFocus,
  compareValues,
  onToggleCompare,
  onSelectValues,
  customMetrics,
  columnFilters,
  onColumnFiltersChange,
}: ClicksGridProps) {
  // 2+ grouping dimensions: the dimension column is a composite "a ▸ b" label.
  // Drill-down, compare and expansion key off a single dimension value, so they
  // only apply to single-dimension grouping.
  const multiDim = (groupDims?.length ?? 1) >= 2;
  const dimensionLabel = multiDim
    ? (groupDims ?? []).map(getDimensionLabel).join(" ▸ ")
    : getDimensionLabel(groupBy);
  const expandable = !multiDim && groupBy === "domain";
  const compareSet = useMemo(
    () => new Set(compareValues ?? []),
    [compareValues],
  );
  const copyable = groupBy === "domain" || groupBy === "url";

  const [openRows, setOpenRows] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [menuTarget, setMenuTarget] = useState<CellTarget | null>(null);
  const [sortRules, setSortRules] = useState<SortRule[]>([]);
  // drafts = what the popover is editing; committed = what actually filters.
  // The two are separate so typing does not filter mid-word — you commit with
  // Apply or Enter. Committed comes from the page (the URL), see the props.
  const [drafts, setDrafts] = useState<Record<string, FilterDraft>>({});
  const committed = columnFilters ?? NO_COLUMN_FILTERS;
  // Not a true functional update: the change applies to committed as of this
  // render. Fine while each event makes one change (Apply, Clear); two in one
  // event would lose the first.
  const setCommitted = useCallback(
    (change: (cur: Record<string, ColumnFilter>) => Record<string, ColumnFilter>) =>
      onColumnFiltersChange?.(change(committed)),
    [onColumnFiltersChange, committed],
  );
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  // The grid pages the full result in the browser, so page is local state.
  const [clientPage, setClientPage] = useState(1);

  // Keitaro and GSC metrics live in one table together, side by side — which
  // columns show is the Metrics picker's job, not a source switch.
  const visibleMetrics = useMemo(
    () => getVisibleMetrics(visibleColumns),
    [visibleColumns],
  );

  const dragSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const showBrand = useMemo(
    () =>
      visibleColumns.includes("brand") &&
      data.some((row) => row.dimension_brand),
    [visibleColumns, data],
  );

  const showCampaignGroup = useMemo(
    () =>
      visibleColumns.includes("campaign_group") &&
      data.some((row) => row.dimension_campaign_group),
    [visibleColumns, data],
  );

  // The middle columns (brand, campaign_group, metrics) in display order — the
  // order they sit in visibleColumns. Brand/campaign_group are columns too, so
  // they drag and hide like metrics; only the dimension column is pinned.
  const orderedColumns = useMemo<GridColumn[]>(() => {
    const metricById = new Map(visibleMetrics.map((m) => [m.id, m]));
    const dims = groupDims?.length ? groupDims : [groupBy];
    const out: GridColumn[] = [];
    for (const id of visibleColumns) {
      if (id === "brand") {
        if (showBrand) out.push({ kind: "brand" });
        continue;
      }
      if (id === "campaign_group") {
        if (showCampaignGroup) out.push({ kind: "campaign_group" });
        continue;
      }
      // Domain Tracker fields: per domain, so left out under a grouping
      // without one, as Brand is - "not set" in every row would read as data.
      // The page says which picked columns the grouping leaves out (4E).
      if (isAttributeColumn(id)) {
        if (appliesToGrouping(id, dims)) out.push({ kind: "attr", id });
        continue;
      }
      const metric = metricById.get(id);
      if (metric) out.push({ kind: "metric", metric });
    }
    return out;
  }, [visibleColumns, visibleMetrics, showBrand, showCampaignGroup, groupDims, groupBy]);

  // Drag reorder over that whole set. We rewrite only the sortable ids inside
  // visibleColumns, leaving the rest (e.g. kv_sparkline) in place.
  const handleColumnDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !onVisibleColumnsChange) return;
      const keys = orderedColumns.map(gridColumnKey);
      const from = keys.indexOf(active.id as string);
      const to = keys.indexOf(over.id as string);
      if (from < 0 || to < 0) return;
      const reordered = arrayMove(keys, from, to);
      const sortable = new Set(keys);
      let k = 0;
      const next = visibleColumns.map((id) =>
        sortable.has(id) ? reordered[k++] : id,
      );
      onVisibleColumnsChange(next);
    },
    [orderedColumns, visibleColumns, onVisibleColumnsChange],
  );

  // colId -> the value used for sorting and filtering that column.
  const dimsKey = (groupDims?.length ? groupDims : [groupBy]).join(",");
  const getValue = useCallback(
    (row: CampaignStat, id: string): string | number | null =>
      columnValue(row, id, dimsKey.split(",")),
    [dimsKey],
  );

  const colKind = useCallback(
    (id: string): ColKind => (TEXT_COLUMNS.has(id) ? "text" : "number"),
    [],
  );

  // A new grouping drops filters and sorts whose column changed meaning or left
  // the screen (see keepAfterRegroup). Adjust-state-on-prop-change, not an effect.
  const [lastDims, setLastDims] = useState(dimsKey);
  if (lastDims !== dimsKey) {
    const ctx = {
      prevDims: lastDims.split(","),
      dims: dimsKey.split(","),
      columns: new Set(visibleColumns),
    };
    const keep = (id: string) => keepAfterRegroup(id, ctx);
    setLastDims(dimsKey);
    setDrafts((cur) => Object.fromEntries(Object.entries(cur).filter(([id]) => keep(id))));
    setSortRules((cur) => cur.filter((r) => keep(r.id)));
  }

  // What a column's popover shows: the draft being typed, else the filter in
  // force (from a link or a chip), else nothing.
  const draftOf = useCallback(
    (id: string): FilterDraft | undefined =>
      drafts[id] ?? (committed[id] ? filterToDraft(committed[id]) : undefined),
    [drafts, committed],
  );

  // Commit the draft for one column (Apply / Enter). An incomplete draft clears
  // that column's filter instead.
  const applyFilterDraft = useCallback(
    (id: string) => {
      const f = draftToFilter(colKind(id), draftOf(id));
      setCommitted((cur) => {
        const next = { ...cur };
        if (f) next[id] = f;
        else delete next[id];
        return next;
      });
      setDrafts((cur) => {
        const next = { ...cur };
        delete next[id];
        return next;
      });
      setOpenFilter(null);
    },
    [draftOf, colKind, setCommitted],
  );

  const clearFilter = useCallback((id: string) => {
    setDrafts((cur) => {
      const next = { ...cur };
      delete next[id];
      return next;
    });
    setCommitted((cur) => {
      const next = { ...cur };
      delete next[id];
      return next;
    });
    setOpenFilter(null);
  }, [setCommitted]);

  // keyword volume: one request for ALL rows (so kv_* sort and filter like any
  // metric), keyed by the row's brand/domain/url part. Measured 30.09.2026:
  // 4,984 domains 0.3 s, 924 brands 0.75 s.
  const volIdx = (groupDims?.length ? groupDims : [groupBy]).findIndex((d) =>
    KEYWORD_VOLUME_DIMS.has(d),
  );
  const volDim = volIdx >= 0 ? (groupDims?.length ? groupDims : [groupBy])[volIdx] : undefined;
  const volKey = useCallback(
    (row: CampaignStat): string | null =>
      volIdx < 0 ? null : (row.dimension_parts?.[volIdx] ?? row.dimension_value),
    [volIdx],
  );
  const volumeWanted =
    volDim !== undefined && visibleColumns.some((c) => c.startsWith("kv_") || c === "capture_rate");
  const volumeKeys = useMemo(
    () =>
      volumeWanted
        ? [...new Set(data.map(volKey).filter((v): v is string => !!v))]
        : [],
    [volumeWanted, data, volKey],
  );
  const { data: volumes } = useKeywordVolumes(volDim ?? "", volumeKeys, activeFilters);
  // Capture rate's GSC side: days of the period GSC has. A Country filter narrows
  // the volume to that country but not GSC (all countries), so it shows a dash.
  const { data: gscLatest } = useGscFreshness();
  const gscDays = useMemo(() => {
    if (activeFilters?.country) return 0;
    const { current } = getDateRangeDates(dateRange, new Date(), customFrom, customTo);
    return gscDaysInRange(current.start, current.end, gscLatest);
  }, [activeFilters, dateRange, customFrom, customTo, gscLatest]);
  // Custom metrics last: their formulas may read keyword volume and capture rate.
  const rows = useMemo(() => {
    const merged = volumes?.size
      ? data.map((row) => {
          const key = volKey(row);
          const vm = volumeMetrics(key ? volumes.get(key) : undefined, volumes.values().next().value?.anchor);
          return { ...row, ...vm, capture_rate: captureRate(row.gsc_clicks, vm.kv_last, gscDays) };
        })
      : data;
    return applyCustomMetrics(merged, customMetrics ?? [], (row) => ({ uepc: uepc(row) }));
  }, [data, volumes, volKey, gscDays, customMetrics]);

  // The rows actually shown: the current server page, filtered then sorted in
  // the browser. Sorting/filtering acts on the loaded page for now; fetch-all
  // (next) makes it act on every row and moves paging client-side.
  // Rows passing the column filters, sorted - what "select all" selects.
  const displayedRows = useMemo(() => {
    let shown = filterRows(rows, committed, getValue);
    if (sortRules.length)
      shown = [...shown].sort(compareByRules(sortRules, getValue));
    return shown;
  }, [rows, committed, sortRules, getValue]);

  const shownValues = useMemo(
    () => displayedRows.map((r) => r.dimension_value),
    [displayedRows],
  );
  const headerSelect = selectAllState(shownValues, compareSet);
  // Selected rows in the current sort order, filtered out or not.
  const selectedRows = useMemo(
    () => selectedRowsInOrder(rows, compareSet, sortRules, getValue),
    [rows, sortRules, getValue, compareSet],
  );

  // Filtered+sorted count drives paging; a page past the end clamps.
  const totalCount = displayedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Reset to page 1 whenever the rows change (new data, filter or sort) —
  // otherwise a filter down to 3 rows strands you on page 40. Done during
  // render (React's "adjust state when inputs change" pattern), not an effect,
  // which would cascade renders.
  const rowsKey = `${JSON.stringify(committed)}|${JSON.stringify(sortRules)}|${pageSize}`;
  const [pageReset, setPageReset] = useState({ data, key: rowsKey });
  if (pageReset.data !== data || pageReset.key !== rowsKey) {
    setPageReset({ data, key: rowsKey });
    setClientPage(1);
  }

  const pageForRender = Math.min(clientPage, totalPages);
  const pagedRows = displayedRows.slice(
    (pageForRender - 1) * pageSize,
    pageForRender * pageSize,
  );

  // keyword volume: the sparkline column, picked as "kv_sparkline".
  const volumeOn = volDim !== undefined && visibleColumns.includes("kv_sparkline");
  const volumeAnchor = volumes?.values().next().value?.anchor;
  const volumeCols = volumeOn ? 1 : 0;

  // Current filtered rows for "copy column as list". Closes over displayedRows
  // directly — no render-time ref read (that returned the previous render's
  // rows). The menu openers re-create when the rows change, which is fine: the
  // rows re-render on a filter/sort anyway.
  const columnValuesFor = useCallback(
    (id: string): string[] =>
      displayedRows.map((row) => {
        const v = getValue(row, id);
        return v == null ? "" : String(v);
      }),
    [displayedRows, getValue],
  );

  const toggleRow = useCallback((value: string) => {
    setOpenRows((cur) => {
      const next = new Set(cur);
      if (!next.delete(value)) next.add(value);
      return next;
    });
  }, []);

  const prevByDimension = useMemo(() => {
    if (!compareEnabled || !previousData?.length) return undefined;
    return new Map(previousData.map((row) => [row.dimension_value, row]));
  }, [compareEnabled, previousData]);

  const openCellMenu = useCallback(
    (
      event: React.MouseEvent,
      cell: {
        value: string;
        columnLabel: string;
        kind: "dimension" | "brand" | "campaign_group" | "attr" | "metric";
      },
    ) => {
      event.preventDefault();
      // A row can sit under several groups ("crossbrand, trustpilot"); that
      // string is no single group, so it cannot be filtered or grouped on.
      const attr = cell.kind === "brand" || cell.kind === "campaign_group" ? cell.kind : undefined;
      const attrUsable = attr === "brand" || (attr === "campaign_group" && !cell.value.includes(", "));
      setMenuTarget({
        x: event.clientX,
        y: event.clientY,
        value: cell.value,
        columnLabel: cell.columnLabel,
        filterKey:
          cell.kind === "dimension"
            ? filterKeyForGroupBy(groupBy)
            : attrUsable
              ? attr
              : undefined,
        groupByValue: attr && groupBy !== attr ? attr : undefined,
        columnValues:
          cell.kind === "dimension"
            ? columnValuesFor("dimension")
            : attrUsable && attr
              ? columnValuesFor(attr)
              : undefined,
      });
    },
    [groupBy, columnValuesFor],
  );

  // One field to filter, mark and keep-only on: the grouping's, when there is
  // one - a composite "a ▸ b" row is no single value of any field.
  const selectKey = multiDim ? undefined : filterKeyForGroupBy(groupBy);
  const { update: updateFilters } = useUrlFilters();
  const cellFilter = useCellFilter();
  const quickFilter = useCallback(
    (value: string, mode: "include" | "exclude") => {
      if (selectKey) cellFilter(selectKey, value, mode, dimensionLabel);
    },
    [cellFilter, selectKey, dimensionLabel],
  );
  const selectedValues = useMemo(() => [...compareSet], [compareSet]);
  const canKeepOnly = Boolean(selectKey && keepOnlyValues(activeFilters ?? {}, selectKey, selectedValues));
  const canExclude = Boolean(selectKey && excludeValues(activeFilters ?? {}, selectKey, selectedValues));
  // A "contains" on the grouping's field: there is nothing to exclude values from.
  const selectTextMode = selectKey ? fieldFilterOf(activeFilters ?? {}, selectKey)?.mode : undefined;

  // Row marks (20261030183128), shared by everyone; hidden until deployed.
  const markKey = selectKey;
  const { marks, available: marksOn } = useClicksMarks(markKey);
  const setMark = useSetClicksMark(markKey, {
    reportsReadMarks: Object.keys(activeFilters ?? {}).some(isMarkKey),
  });
  const [markTarget, setMarkTarget] = useState<MarkTarget | null>(null);
  const openMarkMenu = useCallback(
    (event: React.MouseEvent, value: string) => {
      const r = (event.currentTarget as HTMLElement).getBoundingClientRect();
      setMarkTarget({
        x: r.left,
        y: r.bottom,
        value,
        mark: marks?.get(value) ?? null,
        returnFocus: event.currentTarget as HTMLElement,
      });
    },
    [marks],
  );

  const openHeaderMenu = useCallback(
    (
      event: React.MouseEvent,
      header: {
        colId: string;
        columnLabel: string;
        hideColumnId?: string;
        groupByValue?: string;
      },
    ) => {
      event.preventDefault();
      setMenuTarget({
        x: event.clientX,
        y: event.clientY,
        columnLabel: header.columnLabel,
        hideColumnId: header.hideColumnId,
        groupByValue: header.groupByValue,
        columnValues: columnValuesFor(header.colId),
      });
    },
    [columnValuesFor],
  );

  const hideColumn = useCallback(
    (columnId: string) =>
      onVisibleColumnsChange?.(visibleColumns.filter((c) => c !== columnId)),
    [onVisibleColumnsChange, visibleColumns],
  );

  const onSortClick = useCallback(
    (event: React.MouseEvent, id: string) =>
      setSortRules((rules) => applyHeaderClick(rules, id, event.shiftKey)),
    [],
  );

  const onLockClick = useCallback(
    (id: string) => setSortRules((rules) => toggleLock(rules, id)),
    [],
  );

  const setDraft = useCallback((id: string, draft: FilterDraft | null) => {
    setDrafts((cur) => {
      const next = { ...cur };
      if (draft) next[id] = draft;
      else delete next[id];
      return next;
    });
  }, []);

  // Sort priority + direction badge for a column header.
  const sortState = (id: string) => {
    const rule = sortRules.find((r) => r.id === id);
    if (!rule) return { icon: ArrowUpDown, active: false, order: 0 };
    const order =
      sortRules.length > 1
        ? orderedRules(sortRules).findIndex((r) => r.id === id) + 1
        : 0;
    return { icon: rule.desc ? ArrowDown : ArrowUp, active: true, order };
  };

  /** The sort / lock / filter controls shared by every header. */
  // A render function, NOT a nested component: defining a component inside the
  // render remounts it (and its <Input>) on every parent re-render, so the
  // filter box lost focus after each keystroke. Inlining the JSX keeps focus.
  const renderHeader = (
    id: string,
    label: string,
    align: "left" | "right" = "left",
  ) => {
    const kind = colKind(id);
    const rule = sortRules.find((r) => r.id === id);
    const { icon: SortIcon, active, order } = sortState(id);
    const filterActive = Boolean(committed[id]);
    const draft = draftOf(id) ?? {
      op: kind === "text" ? "contains" : "eq",
      a: "",
      b: "",
    };

    return (
      <span
        className={cn(
          "inline-flex min-w-0 items-center gap-1",
          align === "right" && "flex-row-reverse",
        )}
      >
        {/* The label itself sorts — no always-on sort icon eating width.
            Arrow, priority and lock show only when the column is active. */}
        <button
          type="button"
          onClick={(e) => onSortClick(e, id)}
          title="Sort — Shift-click adds a second key"
          className="inline-flex min-w-0 cursor-pointer items-center gap-0.5 hover:text-foreground"
        >
          <span className="truncate">{label}</span>
          {/* Always shown, faint when unsorted — a 3rd click clears the sort,
              and an icon that vanishes reads as "broken" rather than "off". */}
          <SortIcon
            className={cn(
              "size-3.5 shrink-0",
              active ? "text-foreground" : "text-muted-foreground/30",
            )}
          />
          {order > 0 ? (
            <span className="text-[10px] font-semibold">{order}</span>
          ) : null}
          {rule?.locked ? (
            <Lock className="size-3 shrink-0 text-primary" />
          ) : null}
        </button>

        <Popover
          open={openFilter === id}
          onOpenChange={(o) => {
            // A fresh open shows the filter in force, not an old half-typed draft.
            if (o) setDraft(id, null);
            setOpenFilter(o ? id : null);
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Filter & lock"
              className={cn(
                "shrink-0 cursor-pointer rounded p-0.5 hover:bg-muted",
                filterActive || rule?.locked
                  ? "text-primary"
                  : "text-muted-foreground/40",
              )}
            >
              <FilterIcon className="size-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 space-y-2">
            <button
              type="button"
              onClick={() => onLockClick(id)}
              className="flex w-full items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted"
            >
              {rule?.locked ? (
                <Lock className="size-4 text-primary" />
              ) : (
                <LockOpen className="size-4 text-muted-foreground" />
              )}
              {rule?.locked ? "Unlock sort key" : "Lock as sort key"}
            </button>
            <div className="h-px bg-border" />
            <ColumnFilterForm
              kind={kind}
              draft={draft}
              onDraft={(d) => setDraft(id, d)}
              onApply={() => applyFilterDraft(id)}
              onClear={filterActive ? () => clearFilter(id) : undefined}
            />
          </PopoverContent>
        </Popover>
      </span>
    );
  };

  // Export the given rows (every filtered+sorted row, or the selection) with
  // the currently visible columns; the cells are built by clicksCsv.
  const exportCsv = useCallback((exportRows: CampaignStat[], suffix = "") => {
    const csv = Papa.unparse(
      clicksCsv(exportRows, { groupDims, dimensionLabel, columns: orderedColumns }),
    );
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clicks_${(groupDims ?? [groupBy]).join("-")}${suffix}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [orderedColumns, groupDims, groupBy, dimensionLabel]);

  const colSpan =
    visibleMetrics.length + 1 + (showBrand ? 1 : 0) + (showCampaignGroup ? 1 : 0) + volumeCols +
    orderedColumns.filter((c) => c.kind === "attr").length;

  return (
    <div className="flex-1 flex flex-col min-h-0 space-y-4">
      <CellContextMenu
        target={menuTarget}
        onClose={() => setMenuTarget(null)}
        onHideColumn={hideColumn}
        onFocusChart={onChartFocus}
      />

      <MarkMenu
        target={markTarget}
        onClose={() => {
          // Back to the row's mark button (the menu's own trigger is a hidden
          // point) - unless a click went to another control.
          const el = markTarget?.returnFocus;
          setMarkTarget(null);
          if (el)
            requestAnimationFrame(() => {
              if (!document.activeElement || document.activeElement === document.body) el.focus();
            });
        }}
        onSet={(value, mark) => setMark.mutate({ value, mark })}
        onShowOnly={(mark) => {
          if (markKey)
            updateFilters({
              filters: (cur) => withFieldFilter(cur, markFilterKey(markKey), { mode: "include", values: [mark] }),
            });
        }}
      />

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">
            {totalCount.toLocaleString("en-US")}
          </span>
          {/* "rows" after a column filter means the rows that survived it. */}
          {`${totalCount === 1 ? "row" : "rows"}${
            Object.keys(committed).length > 0 ? " (filtered)" : " total"
          }`}
        </div>
        <button
          type="button"
          onClick={() => exportCsv(displayedRows)}
          disabled={totalCount === 0}
          title="Export the filtered, sorted rows to CSV"
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download className="size-3.5" /> Export CSV
        </button>
      </div>

      {compareSet.size > 0 ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs">
          <span className="font-semibold text-foreground">
            {compareSet.size.toLocaleString("en-US")} selected
          </span>
          {compareSet.size > COMPARE_MAX ? (
            <span className="text-muted-foreground">(the chart shows the first {COMPARE_MAX})</span>
          ) : null}
          <span className="mx-1 h-4 w-px bg-border" aria-hidden />
          <button
            type="button"
            onClick={() => exportCsv(selectedRows, "_selected")}
            className="inline-flex cursor-pointer items-center gap-1 rounded px-2 py-0.5 font-medium hover:bg-muted"
          >
            <Download className="size-3.5" /> Export selected
          </button>
          <button
            type="button"
            onClick={() => {
              const list = selectedRows.map((r) => r.dimension_value).join("\n");
              navigator.clipboard.writeText(list).then(
                () => toast.success(`Copied ${selectedRows.length} ${selectedRows.length === 1 ? "value" : "values"}`),
                () => toast.error("Could not copy to the clipboard"),
              );
            }}
            className="inline-flex cursor-pointer items-center gap-1 rounded px-2 py-0.5 font-medium hover:bg-muted"
          >
            <Copy className="size-3.5" /> Copy list
          </button>
          {selectKey ? (
            <>
              <span className="mx-1 h-4 w-px bg-border" aria-hidden />
              <button
                type="button"
                onClick={() => {
                  updateFilters({ filters: (cur) => keepOnlyValues(cur, selectKey, selectedValues) ?? cur });
                  onSelectValues?.([]);
                  toast.success(`Filtered to ${selectedValues.length} selected`, { description: dimensionLabel });
                }}
                disabled={!canKeepOnly}
                title={canKeepOnly ? "Filter the report to the selected rows" : `A filter holds at most ${MAX_FILTER_VALUES} values`}
                className="inline-flex cursor-pointer items-center gap-1 rounded px-2 py-0.5 font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FilterIcon className="size-3.5" /> Keep only
              </button>
              <button
                type="button"
                onClick={() => {
                  updateFilters({ filters: (cur) => excludeValues(cur, selectKey, selectedValues) ?? cur });
                  onSelectValues?.([]);
                  toast.success(`Excluded ${selectedValues.length} selected`, { description: dimensionLabel });
                }}
                disabled={!canExclude}
                title={
                  canExclude
                    ? "Take the selected rows out of the report"
                    : selectTextMode && isTextMode(selectTextMode)
                      ? `The ${dimensionLabel} filter is a “${modeWord(selectTextMode)}” one - change it in its chip`
                      : `Nothing would be left of the ${dimensionLabel} filter, or it would pass ${MAX_FILTER_VALUES} values`
                }
                className="inline-flex cursor-pointer items-center gap-1 rounded px-2 py-0.5 font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FilterX className="size-3.5" /> Exclude
              </button>
            </>
          ) : null}
          <button
            type="button"
            onClick={() => onSelectValues?.([])}
            className="ml-auto inline-flex cursor-pointer items-center gap-1 rounded px-2 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-3.5" /> Clear
          </button>
        </div>
      ) : null}

      {/* clicks-pin-scope / clicks-pin-head (index.css) keep the header row
          pinned to the top of the window while the page scrolls. */}
      <div className="clicks-pin-scope relative flex w-full flex-col overflow-clip rounded-lg border border-border bg-card">
        <DndContext
          sensors={dragSensors}
          collisionDetection={closestCenter}
          onDragEnd={handleColumnDragEnd}
          // The pinned header sits at the top of the window, inside dnd-kit's
          // "scroll up" edge zone, so a column drag would scroll the page to
          // the top. Auto-scroll only the table's own sideways scroll.
          autoScroll={{ canScroll: (el) => el !== document.scrollingElement }}
        >
        <Table>
          {/* Solid background: rows scroll underneath the pinned header. */}
          <TableHeader className="clicks-pin-head relative z-20 border-b border-border bg-muted">
            <TableRow className="border-b border-border hover:bg-transparent">
              <TableHead
                className={cn(
                  CLICKS_CELL_CLASS,
                  "min-w-[300px] font-semibold text-foreground",
                  // Freeze the first column (row identity) during horizontal
                  // scroll; solid bg so other columns don't show through it.
                  "sticky left-0 z-30 bg-muted",
                )}
                onContextMenu={(event) =>
                  openHeaderMenu(event, {
                    colId: "dimension",
                    columnLabel: dimensionLabel,
                  })
                }
              >
                <div className="flex items-center gap-2">
                  {onSelectValues ? (
                    <input
                      type="checkbox"
                      aria-label="Select all rows"
                      title={
                        headerSelect === "all"
                          ? "Unselect all rows"
                          : `Select all ${shownValues.length.toLocaleString("en-US")} rows that pass the filters`
                      }
                      checked={headerSelect === "all"}
                      ref={(el) => {
                        if (el) el.indeterminate = headerSelect === "some";
                      }}
                      onChange={() => onSelectValues(toggleSelectAll(shownValues, compareValues ?? []))}
                      disabled={shownValues.length === 0}
                      className="size-3.5 shrink-0 cursor-pointer accent-primary"
                    />
                  ) : null}
                  {renderHeader("dimension", dimensionLabel)}
                  {/* Native dimension switch on the header (Domain ⇄ URL), only
                      for a single domain/url grouping; sort/filter stay on the
                      label. Other dimensions go through Group By. */}
                  {!multiDim && (groupBy === "domain" || groupBy === "url") && onPrimaryDimChange ? (
                    <PrimaryDimSwitch value={groupBy} onChange={onPrimaryDimChange} />
                  ) : null}
                </div>
              </TableHead>

              {/* Brand, Campaign Group and the metrics are one orderable set —
                  drag any of them; only the grouping-key (dimension) column is
                  pinned. */}
              <SortableContext
                items={orderedColumns.map(gridColumnKey)}
                strategy={horizontalListSortingStrategy}
              >
                {orderedColumns.map((col) => {
                  if (col.kind === "brand")
                    return (
                      <SortableMetricHead
                        key="brand"
                        id="brand"
                        className={cn(
                          CLICKS_CELL_CLASS,
                          "w-[180px] font-semibold text-foreground",
                        )}
                        onContextMenu={(event) =>
                          openHeaderMenu(event, {
                            colId: "brand",
                            columnLabel: "Brand",
                            hideColumnId: "brand",
                            groupByValue:
                              groupBy === "brand" ? undefined : "brand",
                          })
                        }
                      >
                        {renderHeader("brand", "Brand")}
                      </SortableMetricHead>
                    );
                  if (col.kind === "campaign_group")
                    return (
                      <SortableMetricHead
                        key="campaign_group"
                        id="campaign_group"
                        className={cn(
                          CLICKS_CELL_CLASS,
                          "w-[160px] font-semibold text-foreground",
                        )}
                        onContextMenu={(event) =>
                          openHeaderMenu(event, {
                            colId: "campaign_group",
                            columnLabel: "Campaign Group",
                            hideColumnId: "campaign_group",
                            groupByValue: "campaign_group",
                          })
                        }
                      >
                        {renderHeader("campaign_group", "Campaign Group")}
                      </SortableMetricHead>
                    );
                  if (col.kind === "attr")
                    return (
                      <SortableMetricHead
                        key={col.id}
                        id={col.id}
                        className={cn(CLICKS_CELL_CLASS, "font-semibold text-foreground", isNumericAttribute(col.id) && "text-right")}
                        onContextMenu={(event) =>
                          openHeaderMenu(event, {
                            colId: col.id,
                            columnLabel: metricLabel(col.id),
                            hideColumnId: col.id,
                          })
                        }
                      >
                        {renderHeader(col.id, metricLabel(col.id), isNumericAttribute(col.id) ? "right" : "left")}
                      </SortableMetricHead>
                    );
                  const metric = col.metric;
                  return (
                    <SortableMetricHead
                      key={metric.id}
                      id={metric.id}
                      className={cn(
                        CLICKS_CELL_CLASS,
                        "text-right font-semibold",
                        getMetricHeaderTone(metric.id),
                      )}
                      onContextMenu={(event) =>
                        openHeaderMenu(event, {
                          colId: metric.id,
                          columnLabel: metricLabel(metric.id, metric.label),
                          hideColumnId: metric.id,
                        })
                      }
                    >
                      {renderHeader(metric.id, metricLabel(metric.id, metric.label), "right")}
                    </SortableMetricHead>
                  );
                })}
              </SortableContext>

              {/* keyword volume */}
              {volumeOn ? (
                <TableHead className={cn(CLICKS_CELL_CLASS, "font-semibold whitespace-nowrap")}>
                  Keyword Volume
                  <span className="block text-xs font-normal text-muted-foreground">
                    12 months to the row's latest
                  </span>
                </TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody ref={trackBodyHeight}>
            {pagedRows.length > 0 ? (
              pagedRows.map((row) => {
                const isOpen = expandable && openRows.has(row.dimension_value);
                return (
                  <Fragment key={row.dimension_value}>
                    <ClicksTableRow
                      row={row}
                      prevRow={prevByDimension?.get(row.dimension_value)}
                      columns={orderedColumns}
                      dimensionLabel={dimensionLabel}
                      expandable={expandable}
                      copyable={copyable}
                      onCellContextMenu={openCellMenu}
                      isOpen={isOpen}
                      onToggle={() => toggleRow(row.dimension_value)}
                      selectable={Boolean(onToggleCompare)}
                      selected={compareSet.has(row.dimension_value)}
                      onToggleSelect={() => onToggleCompare?.(row.dimension_value)}
                      onQuickFilter={selectKey ? quickFilter : undefined}
                      mark={marksOn ? (marks?.get(row.dimension_value) ?? null) : undefined}
                      onMarkClick={marksOn ? openMarkMenu : undefined}
                      trailing={
                        volumeOn
                          ? (() => {
                              // keyword volume: the sparkline follows the row's own
                              // latest month, or its last value and trend would
                              // read months the row has no data for as zeros.
                              // Per-month values are regular kv_m1..kv_m12 metrics.
                              const series = volumes?.get(volKey(row) ?? "");
                              const ownAnchor = row.kv_last_month ?? volumeAnchor ?? "";
                              return (
                                <TableCell className={CLICKS_CELL_CLASS}>
                                  <KeywordVolumeCell
                                    months={seriesToMonths(series, ownAnchor)}
                                    anchor={ownAnchor}
                                    trend={row.kv_trend_percent ?? null}
                                  />
                                </TableCell>
                              );
                            })()
                          : null
                      }
                    />
                    {isOpen ? (
                      <DomainPages
                        domain={row.dimension_value}
                        dim={groupBy}
                        value={row.dimension_value}
                        dateRange={dateRange}
                        customFrom={customFrom}
                        customTo={customTo}
                        activeFilters={activeFilters}
                        columns={orderedColumns}
                        volumeCols={volumeCols}
                        colSpan={colSpan}
                      />
                    ) : null}
                  </Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={colSpan}
                  className="h-24 text-center text-muted-foreground"
                >
                  No data found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </DndContext>
      </div>

      {totalCount > 0 && (
        <div className="flex-none flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Showing</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => onPageSizeChange(Number(v))}
            >
              <SelectTrigger className="h-9 w-[80px] cursor-pointer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CLICKS_LIMIT_OPTIONS.map((option) => (
                  <SelectItem
                    key={option}
                    value={String(option)}
                    className="cursor-pointer"
                  >
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">
              rows of {totalCount.toLocaleString("en-US")}
            </span>
          </div>

          {totalPages > 1 && (
            <PaginationControls
              currentPage={pageForRender}
              totalPages={totalPages}
              onPageChange={setClientPage}
            />
          )}
        </div>
      )}
    </div>
  );
}
